import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'aws-amplify/auth';
import { logEvent } from '../api';

const WARNING_AFTER_MS = 10 * 60 * 1000;   // show warning after 10 minutes idle
const LOGOUT_AFTER_MS = 15 * 60 * 1000;    // log out after 15 minutes idle
const COUNTDOWN_SECONDS = 5 * 60;          // 5-minute countdown in the warning

function IdleTimeout({ children }) {
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);
  const navigate = useNavigate();

  // Refs always hold the latest values, so event listeners never use stale data
  const showWarningRef = useRef(false);
  const warningTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const warningStartTimeRef = useRef(null);
  const loggingOutRef = useRef(false);

  const clearAllTimers = () => {
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  };

  const handleLogout = useCallback(async () => {
    if (loggingOutRef.current) return;
    loggingOutRef.current = true;
    clearAllTimers();
    showWarningRef.current = false;
    setShowWarning(false);
    await logEvent('logout');
    try {
      await signOut();
    } catch {
      // Ignore
    }
    navigate('/login', { replace: true });
  }, [navigate]);

  const updateCountdown = useCallback(() => {
    if (!warningStartTimeRef.current) return;
    const elapsed = Math.floor((Date.now() - warningStartTimeRef.current) / 1000);
    const remaining = COUNTDOWN_SECONDS - elapsed;
    if (remaining <= 0) {
      setSecondsLeft(0);
      handleLogout();
    } else {
      setSecondsLeft(remaining);
    }
  }, [handleLogout]);

  const startWarningCountdown = useCallback((startedAt = Date.now()) => {
    clearAllTimers();
    showWarningRef.current = true;
    setShowWarning(true);
    warningStartTimeRef.current = startedAt;
    updateCountdown();
    countdownIntervalRef.current = setInterval(updateCountdown, 1000);
  }, [updateCountdown]);

  const scheduleWarning = useCallback((delayMs) => {
    clearAllTimers();
    warningTimerRef.current = setTimeout(() => startWarningCountdown(), delayMs);
  }, [startWarningCountdown]);

  // Any mouse/keyboard activity resets the idle timer, but NOT while the warning is showing
  const handleActivity = useCallback(() => {
    if (showWarningRef.current || loggingOutRef.current) return;
    lastActivityRef.current = Date.now();
    scheduleWarning(WARNING_AFTER_MS);
  }, [scheduleWarning]);

  const handleStayLoggedIn = () => {
    showWarningRef.current = false;
    setShowWarning(false);
    warningStartTimeRef.current = null;
    lastActivityRef.current = Date.now();
    setSecondsLeft(COUNTDOWN_SECONDS);
    scheduleWarning(WARNING_AFTER_MS);
  };

  // When the user comes back to this tab, recalculate based on the real clock
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible' || loggingOutRef.current) return;

      if (showWarningRef.current) {
        updateCountdown();
        if (!countdownIntervalRef.current && !loggingOutRef.current) {
          countdownIntervalRef.current = setInterval(updateCountdown, 1000);
        }
        return;
      }

      const idleTime = Date.now() - lastActivityRef.current;
      if (idleTime >= LOGOUT_AFTER_MS) {
        handleLogout();
      } else if (idleTime >= WARNING_AFTER_MS) {
        // Start the countdown as if the warning appeared on time
        startWarningCountdown(lastActivityRef.current + WARNING_AFTER_MS);
      } else {
        scheduleWarning(WARNING_AFTER_MS - idleTime);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [handleLogout, startWarningCountdown, scheduleWarning, updateCountdown]);

  // Listen for user activity
  useEffect(() => {
    const activityEvents = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    activityEvents.forEach((event) => window.addEventListener(event, handleActivity));
    return () => {
      activityEvents.forEach((event) => window.removeEventListener(event, handleActivity));
    };
  }, [handleActivity]);

  // Start the idle timer when the page first loads; clean up when leaving
  useEffect(() => {
    scheduleWarning(WARNING_AFTER_MS);
    return () => clearAllTimers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatTime = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <>
      {children}
      {showWarning && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '32px',
            borderRadius: '10px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            width: '400px',
            textAlign: 'center'
          }}>
            <h2 style={{ color: '#1a365d', marginBottom: '12px' }}>
              Are you still there?
            </h2>
            <p style={{ color: '#666', marginBottom: '8px' }}>
              You've been inactive for a while. For security, you'll be logged out automatically in:
            </p>
            <p style={{
              fontSize: '32px',
              fontWeight: 'bold',
              color: '#e53e3e',
              margin: '16px 0'
            }}>
              {formatTime(secondsLeft)}
            </p>
            <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
              <button
                onClick={handleLogout}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: 'white',
                  color: '#1a365d',
                  border: '1px solid #1a365d',
                  borderRadius: '6px',
                  fontSize: '16px',
                  cursor: 'pointer'
                }}
              >
                Log Out Now
              </button>
              <button
                onClick={handleStayLoggedIn}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#1a365d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '16px',
                  cursor: 'pointer'
                }}
              >
                Stay Logged In
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default IdleTimeout;